import { Component } from '@theme/component';
import { fetchConfig, onAnimationEnd, preloadImage } from '@theme/utilities';
import { ThemeEvents, CartAddEvent, CartErrorEvent, VariantUpdateEvent } from '@theme/events';
import { cartPerformance } from '@theme/performance';
import { morph } from '@theme/morph';

export const ADD_TO_CART_TEXT_ANIMATION_DURATION = 2000;

/**
 * A custom element that manages an add to cart button.
 *
 * @typedef {object} AddToCartRefs
 * @property {HTMLButtonElement} addToCartButton - The add to cart button.
 * @extends Component<AddToCartRefs>
 */
export class AddToCartComponent extends Component {
  requiredRefs = ['addToCartButton'];

  /** @type {number | undefined} */
  #animationTimeout;

  /** @type {number | undefined} */
  #cleanupTimeout;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('pointerenter', this.#preloadImage);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.#animationTimeout) clearTimeout(this.#animationTimeout);
    if (this.#cleanupTimeout) clearTimeout(this.#cleanupTimeout);
    this.removeEventListener('pointerenter', this.#preloadImage);
    this.refs.addToCartButton?.classList.remove('atc-added');
  }

  /**
   * Disables the add to cart button.
   */
  disable() {
    this.refs.addToCartButton.disabled = true;
  }

  /**
   * Enables the add to cart button.
   */
  enable() {
    this.refs.addToCartButton.disabled = false;
  }

  /**
   * Handles the click event for the add to cart button.
   * @param {MouseEvent & {target: HTMLElement}} event - The click event.
   */
  handleClick(event) {
    const form = this.closest('form');
    if (!form?.checkValidity()) return;
    // A repeat tap while the first add is in flight adds nothing (product-form-component's guard),
    // so it must not restart the "Added" flash or fly a second image to the cart.
    if (this.refs.addToCartButton?.getAttribute('aria-busy') === 'true') return;

    // component.js calls this from its capture listener, before the v2 PDP engraving guard
    // (xz-v2-ws-pdp.js) can cancel the click. Decide once the click has been dispatched: a
    // cancelled click adds nothing, so the button must not flash "Added".
    const target = event.target;
    setTimeout(() => {
      if (event.defaultPrevented) return;

      this.animateAddToCart();

      const animationEnabled = this.dataset.addToCartAnimation === 'true';

      if (animationEnabled && !target.closest('.quick-add-modal')) {
        this.#animateFlyToCart();
      }
    }, 0);
  }

  #preloadImage = () => {
    const image = this.dataset.productVariantMedia;

    if (!image) return;

    preloadImage(image);
  };

  /**
   * Animates the fly to cart animation.
   */
  #animateFlyToCart() {
    const { addToCartButton } = this.refs;
    const cartIcon = document.querySelector('.header-actions__cart-icon');

    const image = this.dataset.productVariantMedia;

    if (!cartIcon || !addToCartButton || !image) return;

    const flyToCartElement = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));

    flyToCartElement.style.setProperty('background-image', `url(${image})`);
    flyToCartElement.source = addToCartButton;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);
  }

  /**
   * Animates the add to cart button.
   */
  animateAddToCart() {
    const { addToCartButton } = this.refs;

    if (this.#animationTimeout) clearTimeout(this.#animationTimeout);
    if (this.#cleanupTimeout) clearTimeout(this.#cleanupTimeout);

    if (!addToCartButton.classList.contains('atc-added')) {
      addToCartButton.classList.add('atc-added');
    }

    this.#animationTimeout = setTimeout(() => {
      this.#cleanupTimeout = setTimeout(() => {
        this.refs.addToCartButton.classList.remove('atc-added');
      }, 10);
    }, ADD_TO_CART_TEXT_ANIMATION_DURATION);
  }
}

if (!customElements.get('add-to-cart-component')) {
  customElements.define('add-to-cart-component', AddToCartComponent);
}

/**
 * A custom element that manages a product form.
 *
 * @typedef {object} ProductFormRefs
 * @property {HTMLInputElement} variantId - The form input for submitting the variant ID.
 * @property {AddToCartComponent | undefined} addToCartButtonContainer - The add to cart button container element.
 * @property {HTMLElement | undefined} addToCartTextError - The add to cart text error.
 * @property {HTMLElement | undefined} acceleratedCheckoutButtonContainer - The accelerated checkout button container element.
 * @property {HTMLElement} liveRegion - The live region.
 *
 * @extends Component<ProductFormRefs>
 */
class ProductFormComponent extends Component {
  requiredRefs = ['variantId', 'liveRegion'];
  #abortController = new AbortController();

  /** @type {number | undefined} */
  #timeout;

  /**
   * An add is in flight. A second submit while it is pending (a double tap on the button, the v2 sticky
   * bar's buy() clicking it again) adds nothing: one tap, one knife.
   */
  #adding = false;

  connectedCallback() {
    super.connectedCallback();

    // Native editor moves reconnect the same element after disconnect aborts it.
    if (this.#abortController.signal.aborted) this.#abortController = new AbortController();
    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section, dialog, product-card');
    target?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, { signal });
    target?.addEventListener(ThemeEvents.variantSelected, this.#onVariantSelected, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
  }

  /**
   * Handles the submit event for the product form.
   *
   * @param {Event} event - The submit event.
   */
  handleSubmit(event) {
    event.preventDefault();
    if (this.#adding) return;

    const form = this.querySelector("form");
    const formData = new FormData(form);
    const quantity = Number(formData.get("quantity")) || 1;
    const variantId = formData.get("id");

    // Pre-order: buy-buttons.liquid sets data-preorder-property on this
    // component for pre-order variants only (preorder-info.js keeps it in
    // sync on variant change). Hidden Liquid inputs never reach this JSON
    // payload, so the cart line property is injected here.
    const preorderProperty = this.dataset.preorderProperty;

    // Pre-order + engraving excluded v1 (ship-date math wrong for engraved).
    const accessoryForm = this.dataset.v2AccessoryForm === "true";
    const SHOW_FEE = !accessoryForm && window.engravingSelected && String(window.engravingText || "").trim() && !preorderProperty;
    const SHOW_FEE2 = window.engravingSecondSelected && String(window.engravingText2 || "").trim();
    const FEE_ID = 43781283217459;
    const FEE_ID2 = 43781283250227;
    // window.knife_num is set synchronously at page load by engraving-option.liquid,
    // but is undefined/NaN if the product's custom.knife_num metafield is blank.
    // Default to 1 so the engraving-fee quantity is never NaN.
    const knife_num = Number(window.knife_num) || 1;

    // Properties this handler owns. They are set from the window.* engraving
    // state below, never copied from the form: the Engraving Font <select>
    // carries name="properties[Engraving Font]" and would otherwise be sent on
    // every unengraved knife.
    const OWNED_PROPERTIES = ["Engraving Text", "Engraving Text2", "Engraving Font", "Knife Quantity", "Pre-order"];

    const buildProperties = () => {
      // Typed as an open record: the keys below are added conditionally, and
      // without this the checker infers the literal shape from the initialiser
      // and flags every later assignment.
      /** @type {Record<string, string | number>} */
      const props = {};
      // Every other properties[...] field in the form goes through as it would on a
      // native submit: the gift card's Recipient email / name, Message, Send on,
      // __shopify_send_gift_card_to_recipient and __shopify_offset were dropped
      // here before, so the card went to the buyer and the message was lost.
      // FormData skips disabled inputs, and gift-card-recipient-form.js disables
      // the recipient fields (and removes the control flag) while "My email" is
      // chosen. Empty values are left out, as a native submit leaves them blank.
      for (const [key, value] of typeof formData.entries === "function" ? formData.entries() : []) {
        const match = /^properties\[(.+)\]$/.exec(key);
        if (!match || typeof value !== "string" || !value.trim()) continue;
        if (OWNED_PROPERTIES.includes(match[1])) continue;
        props[match[1]] = value;
      }
      if (preorderProperty) props["Pre-order"] = preorderProperty;
      if (!SHOW_FEE) return props;

      // "Knife Quantity" is the engraving-fee multiplier for a set. Every reader (cart-engraving.js,
      // component-cart-items.js, snippets/xz-v2-cart-line.liquid, the live theme's cart) takes a
      // missing value as 1, so it goes only on engraved sets: an unengraved knife then carries the
      // same (empty) properties as a card / quick-add / upsell add and merges into one cart line,
      // and checkout no longer shows the shopper "Knife Quantity: 1".
      if (knife_num > 1) props["Knife Quantity"] = knife_num;
      // Trimmed: the boxes keep a trailing space while the shopper is typing.
      props["Engraving Text"] = String(window.engravingText).trim();
      if (SHOW_FEE2) props["Engraving Text2"] = String(window.engravingText2).trim();
      // The face the customer chose (Plain / Fancy). The <select> in
      // engraving-option.liquid carries name="properties[Engraving Font]", but
      // this handler calls preventDefault() and builds the JSON payload itself,
      // so that attribute is never read — without this line the choice reaches
      // the preview and nothing else, and the order records no font at all.
      // window.engravingFont is set at page load to the admin-configured font
      // and updated on change, so it is populated even when the chooser is
      // hidden by the theme gate.
      if (window.engravingFont) props["Engraving Font"] = window.engravingFont;
      return props;
    };

    // Discover the real section id(s) of every mounted cart-items-component.
    // In this (Horizon) theme the cart drawer AND the cart-icon bubble both
    // live inside the header section (data-section-id="sections--…__header_section"),
    // so morphing that one section repaints both — no empty-drawer flash.
    // The names "cart-drawer"/"cart-icon-bubble" are NOT real sections here
    // (they return null), which is why the old fallback fetch raced the open animation.
    const getSectionIdsToUpdate = () => {
      const sectionsToUpdate = new Set();
      document.querySelectorAll("cart-items-component").forEach((el) => {
        if (el instanceof HTMLElement && el.dataset.sectionId) {
          sectionsToUpdate.add(el.dataset.sectionId);
        }
      });
      return Array.from(sectionsToUpdate);
    };

    // The knife and its engraving fee go in ONE /cart/add.js request (items[]), so the cart can never hold
    // an engraved knife without its fee: two sequential adds left the knife in the cart when the fee add
    // alone failed (r3, a silent $19-$87 shortfall at checkout). A refused request adds neither (over stock
    // Shopify can add part of it; the fee sync in the catch below corrects that). The add also asks for the
    // section HTML, so the drawer morphs without a second round-trip.
    const addToCart = () => {
      const sectionIds = getSectionIdsToUpdate();
      const line = { id: variantId, quantity, properties: buildProperties() };
      /** @type {Record<string, unknown>} */
      const body = SHOW_FEE
        ? { items: [line, { id: SHOW_FEE2 ? FEE_ID2 : FEE_ID, quantity: quantity * knife_num }] }
        : line;
      if (sectionIds.length > 0) {
        body.sections = sectionIds.join(",");
        body.sections_url = window.location.pathname;
      }
      return fetch("/cart/add.js", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body)
      });
    };

    // Shopper-visible count: engraving-fee lines are hidden slots, so they never count
    // (matches snippets/xz-v2-cart-count.liquid and the rendered [ref="cartItemCount"]).
    const FEE_VARIANT_IDS = [43781283217459, 43781283250227];
    const visibleItemCount = (cart) =>
      ((cart && cart.items) || []).reduce(
        (n, item) => (FEE_VARIANT_IDS.includes(item.variant_id) ? n : n + item.quantity),
        0
      );
    const countFromSections = (sections) => {
      for (const html of Object.values(sections || {})) {
        if (!html) continue;
        const countEl = new DOMParser().parseFromString(html, "text/html").querySelector('[ref="cartItemCount"]');
        if (countEl) return parseInt(countEl.textContent, 10) || 0;
      }
      return null;
    };

    const getCartWithSections = async () => {
      const sectionsToUpdate = getSectionIdsToUpdate();

      if (sectionsToUpdate.length === 0) {
        const cartRes = await fetch("/cart.js");
        if (!cartRes.ok) throw new Error(`Cart fetch failed with status ${cartRes.status}`);
        const cart = await cartRes.json();
        return { itemCount: visibleItemCount(cart), sections: {} };
      }

      const res = await fetch("/cart/update.js", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          updates: {},
          sections: sectionsToUpdate.join(","),
          sections_url: window.location.pathname,
        }),
      });
      if (!res.ok) throw new Error(`Cart refresh failed with status ${res.status}`);
      const data = await res.json();
      const sections = data.sections ?? {};
      const parsed = countFromSections(sections);
      return { itemCount: parsed ?? visibleItemCount(data), sections };
    };

    // What the shopper reads when an add fails: Shopify's own words (the description for sold out or the cart
    // limit, the message for a gift card recipient error) as they are; anything else (no connection, a
    // non-JSON or description-less answer) gets plain copy instead of a raw "Failed to fetch" / "Cart Error".
    const ADD_FAILED = "Couldn't add to cart. Check your connection and try again.";
    let added = false;

    this.#setAdding(true);
    // Through a resolved promise, so a synchronous throw in addToCart still reaches the catch
    // and the finally that releases the in-flight flag.
    Promise.resolve()
      .then(addToCart)
      .then(async (response) => {
        // fetch() does not reject on HTTP 422 (out-of-stock / max qty / bad variant).
        if (!response.ok) {
          const error = await response.json().catch(() => ({}));
          // Per-field errors (gift card recipient email / send-on date) go to the
          // recipient form, which marks the field; it is the only cartError listener.
          if (error.errors && typeof error.errors === "object") {
            this.dispatchEvent(new CartErrorEvent(this.id, error.message, error.description, error.errors));
          }
          // Field errors (a gift card recipient) carry an object description and the readable text in message
          // ("Validation failed: Email is invalid").
          const description = typeof error.description === "string" ? error.description.trim() : "";
          const fieldMessage =
            error.errors && typeof error.errors === "object" && typeof error.message === "string" ? error.message.trim() : "";
          const shopperMessage = description || fieldMessage || ADD_FAILED;
          throw Object.assign(new Error(shopperMessage), { shopperMessage });
        }
        added = true;

        const addData = await response.json().catch(() => ({}));
        const addSections =
          addData && addData.sections && Object.keys(addData.sections).length > 0
            ? addData.sections
            : null;

        let itemCount;
        let sections;
        // The item count rides inside the rendered section HTML (the hidden
        // [ref="cartItemCount"] element). The sections are rendered after the whole
        // request (knife and fee), so the add response is sufficient on its own.
        const parsedCount = addSections ? countFromSections(addSections) : null;
        if (!addSections || parsedCount === null) {
          // No sections payload (no cart on this page): read the cart instead.
          ({ itemCount, sections } = await getCartWithSections());
        } else {
          sections = addSections;
          itemCount = parsedCount;
        }

        this.#clearAddToCartError();
        const eventTarget = accessoryForm && this.isConnected ? this : document;
        eventTarget.dispatchEvent(
          new CartAddEvent({}, variantId, {
            source: "product-form-component",
            itemCount,
            productId: variantId,
            sections,
          })
        );
      })
      .catch((error) => {
        this.#showAddToCartError(
          error?.shopperMessage || (added ? "Added to cart. Open your cart to review it." : ADD_FAILED)
        );
        // A refused knife + fee request can still have changed the cart: over stock, Shopify adds what it
        // can of each line and answers 422 ("Only 50 items were added to your cart due to availability."),
        // and the untracked fee is never capped. Put the fees back in step with the engraved knives
        // (cart-engraving.js: one cart read, a write and a quiet repaint only when they differ).
        if (SHOW_FEE && !added) window.CartEngraving?.cleanup?.();
      })
      .finally(() => this.#setAdding(false));
  }

  /**
   * Marks an add in flight, mirrored as aria-busy on the add to cart button (the v2 sticky bar
   * reads it and stays quiet until the add has finished).
   * @param {boolean} adding
   */
  #setAdding(adding) {
    this.#adding = adding;
    const button = this.refs.addToCartButtonContainer?.refs.addToCartButton;
    if (!(button instanceof HTMLElement)) return;
    if (adding) button.setAttribute("aria-busy", "true");
    else button.removeAttribute("aria-busy");
  }

  /**
   * Surfaces an add-to-cart failure using the theme's existing error refs
   * (the assertive live region + the hidden product-form error span).
   * @param {string} message
   */
  #showAddToCartError(message) {
    const { addToCartTextError, addToCartButtonContainer } = this.refs;
    addToCartButtonContainer?.refs.addToCartButton?.classList.remove("atc-added");
    this.#setLiveRegionText(message);
    if (!(addToCartTextError instanceof HTMLElement)) return;
    let messageEl = addToCartTextError.querySelector(".product-form-text__error-message");
    if (!messageEl) {
      messageEl = document.createElement("span");
      messageEl.className = "product-form-text__error-message";
      addToCartTextError.appendChild(messageEl);
    }
    messageEl.textContent = message;
    addToCartTextError.classList.remove("hidden");
  }

  /**
   * Clears a previously-shown add-to-cart error.
   */
  #clearAddToCartError() {
    const { addToCartTextError } = this.refs;
    if (addToCartTextError instanceof HTMLElement) addToCartTextError.classList.add("hidden");
    this.#clearLiveRegionText();
  }

  /**
   * @param {*} text
   */
  #setLiveRegionText(text) {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = text;
  }

  #clearLiveRegionText() {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = '';
  }

  /**
   * @param {VariantUpdateEvent} event
   */
  #onVariantUpdate = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.detail.data.productId !== this.dataset.productId) {
      return;
    }

    const { variantId, addToCartButtonContainer } = this.refs;

    const currentAddToCartButton = addToCartButtonContainer?.refs.addToCartButton;
    const newAddToCartButton = event.detail.data.html.querySelector('[ref="addToCartButton"]');

    if (!currentAddToCartButton) return;

    // Update the button state
    if (event.detail.resource == null || event.detail.resource.available == false) {
      addToCartButtonContainer.disable();
      this.refs.acceleratedCheckoutButtonContainer?.setAttribute('hidden', 'true');
    } else {
      addToCartButtonContainer.enable();
      this.refs.acceleratedCheckoutButtonContainer?.removeAttribute('hidden');
    }

    // Update the add to cart button text and icon
    if (newAddToCartButton) {
      morph(currentAddToCartButton, newAddToCartButton);
    }

    // Update the variant ID
    variantId.value = event.detail.resource?.id ?? '';

    // Set the data attribute for the add to cart button to the product variant media if it exists
    if (event.detail.resource) {
      const productVariantMedia = event.detail.resource.featured_media?.preview_image?.src;
      productVariantMedia &&
        addToCartButtonContainer?.setAttribute('data-product-variant-media', productVariantMedia + '&width=100');
    }
  };

  /**
   * Disable the add to cart button while the UI is updating before #onVariantUpdate is called.
   * Accelerated checkout button is also disabled via its own event listener not exposed to the theme.
   */
  #onVariantSelected = () => {
    this.refs.addToCartButtonContainer?.disable();
  };
}

if (!customElements.get('product-form-component')) {
  customElements.define('product-form-component', ProductFormComponent);
}

class FlyToCart extends HTMLElement {
  /** @type {Element} */
  source;

  /** @type {Element} */
  destination;

  connectedCallback() {
    this.#animate();
  }

  #animate() {
    const rect = this.getBoundingClientRect();
    const sourceRect = this.source.getBoundingClientRect();
    const destinationRect = this.destination.getBoundingClientRect();

    //Define bezier curve points
    // Maybe add half of the size of the flying thingy to the x and y to make it center properly
    const offset = {
      x: rect.width / 2,
      y: rect.height / 2,
    };
    const startPoint = {
      x: sourceRect.left + sourceRect.width / 2 - offset.x,
      y: sourceRect.top + sourceRect.height / 2 - offset.y,
    };

    const endPoint = {
      x: destinationRect.left + destinationRect.width / 2 - offset.x,
      y: destinationRect.top + destinationRect.height / 2 - offset.y,
    };

    //Calculate the control points
    const controlPoint1 = { x: startPoint.x, y: startPoint.y - 200 }; // Go up 200px
    const controlPoint2 = { x: endPoint.x - 300, y: endPoint.y - 100 }; // Go left 300px and up 100px

    //Animation variables
    /** @type {number | null} */
    let startTime = null;
    const duration = 600; // 600ms

    this.style.opacity = '1';

    /**
     * Animates the flying thingy along the bezier curve.
     * @param {number} currentTime - The current time.
     */
    const animate = (currentTime) => {
      if (!startTime) startTime = currentTime;
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);

      // Calculate current position along the bezier curve
      const position = bezierPoint(progress, startPoint, controlPoint1, controlPoint2, endPoint);

      //Update the position of the flying thingy
      this.style.setProperty('--x', `${position.x}px`);
      this.style.setProperty('--y', `${position.y}px`);

      // Scale down as it approaches the cart
      const scale = 1 - progress * 0.5;
      this.style.setProperty('--scale', `${scale}`);

      //Continue the animation if not finished
      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        //Fade out the flying thingy
        this.style.opacity = '0';
        onAnimationEnd(this, () => this.remove());
      }
    };

    // Position the flying thingy back to the start point
    this.style.setProperty('--x', `${startPoint.x}px`);
    this.style.setProperty('--y', `${startPoint.y}px`);

    //Start the animation
    requestAnimationFrame(animate);
  }
}

/**
 * Calculates a point on a cubic Bézier curve.
 * @param {number} t - The parameter value (0 <= t <= 1).
 * @param {{x: number, y: number}} p0 - The starting point (x, y).
 * @param {{x: number, y: number}} p1 - The first control point (x, y).
 * @param {{x: number, y: number}} p2 - The second control point (x, y).
 * @param {{x: number, y: number}} p3 - The ending point (x, y).
 * @returns {{x: number, y: number}} The point on the curve.
 */
function bezierPoint(t, p0, p1, p2, p3) {
  const cX = 3 * (p1.x - p0.x);
  const bX = 3 * (p2.x - p1.x) - cX;
  const aX = p3.x - p0.x - cX - bX;

  const cY = 3 * (p1.y - p0.y);
  const bY = 3 * (p2.y - p1.y) - cY;
  const aY = p3.y - p0.y - cY - bY;

  const x = aX * Math.pow(t, 3) + bX * Math.pow(t, 2) + cX * t + p0.x;
  const y = aY * Math.pow(t, 3) + bY * Math.pow(t, 2) + cY * t + p0.y;

  return { x, y };
}

if (!customElements.get('fly-to-cart')) {
  customElements.define('fly-to-cart', FlyToCart);
}
